import { createContext, useContext } from 'react';
import { formatKrw, formatSignedNumber, formatUsdPrice } from '@/lib/format';

/**
 * 저점매수 화면의 가격 통화. (2026-09-28, 122번 12.14 · 미국 저점매수)
 *
 * 🔴 두 층을 가른다
 *   합계(시드 · 예수금 · 총자산 · 손익 · 청산 예상액)는 **늘 원화**다 — 미국 계좌도 원화 2,000만으로 굴린다.
 *   종목 가격(평단 · 현재가 · 목표가 · 손절선 · 기준가 · 청산가 · 청산 손익)은 **그 시장의 통화**다 — 미국은 달러.
 *
 * 🔴 왜 컨텍스트인가
 *   BandPanel 안의 작은 컴포넌트(보유 · 감시 · 청산 줄)가 각자 가격을 그린다. 통화를 인자로 줄줄이 넘기면
 *   한 곳을 빠뜨렸을 때 달러 값이 "원" 으로 조용히 찍힌다. 패널 맨 위에서 한 번 정하고 아래는 꺼내 쓴다.
 */
export interface BandPriceFormat {
  /** 달러 계좌인가 */
  usd: boolean;
  /** 가격 한 칸 — 국내 "127,400원" · 미국 "$99.00" */
  px: (value: number | null | undefined) => string;
  /** 부호 붙은 가격 차 — 국내 "+26,736원" · 미국 "+$27.97" */
  signedPx: (value: number | null | undefined) => string;
}

export function bandPriceFormat(currency: 'KRW' | 'USD' | undefined): BandPriceFormat {
  if (currency === 'USD') {
    return {
      usd: true,
      px: formatUsdPrice,
      signedPx: (value) =>
        value === null || value === undefined || !Number.isFinite(value)
          ? '-'
          : `${value > 0 ? '+' : ''}${formatUsdPrice(value)}`,
    };
  }
  return {
    usd: false,
    px: formatKrw,
    signedPx: (value) =>
      value === null || value === undefined || !Number.isFinite(value)
        ? '-'
        : `${formatSignedNumber(Math.round(value))}원`,
  };
}

/** 기본은 원화 — 국내 화면은 이 값 그대로다(Provider 없이 그려도 전과 같다) */
export const BandPriceFormatContext = createContext<BandPriceFormat>(bandPriceFormat('KRW'));

export function useBandPriceFormat(): BandPriceFormat {
  return useContext(BandPriceFormatContext);
}
